import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'

import axios from 'axios'
import qs from 'qs'

import { slugifyString } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import ImportingNotice from './ImportingNotice'
import { importErrorMessage } from './importError'


const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function AmazonProductsImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('100')

    const [productUrl, setProductUrl] = React.useState('')

    const [searchSite, setSearchSite] = React.useState('amazon.com')

    const [categorizeBy, setCategorizeBy] = React.useState('sentiment')

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const menuItems = generateTextImportItems()

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('amazonProducts') ||
            textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleProductUrlChange = event => {
        setProductUrl(event.target.value)
        setProductAsin('')
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('shortTextImport')
                break
            case 2: // Settings
                if (!presetContext) {
                    setSelectedContext(generatePresetContext(searchString))
                }
                break
            case 3: // Analysis
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from Amazon for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const dataToSend = {
            searchQuery: searchString,
            limit: numberOfResults,
            source: searchSite,
            categorizeby: categorizeBy,
            settings: 'morphemes',
            searchType: 'search',
            context: selectedContext,
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post('/import/amazon-keywords', dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(2)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Amazon Keywords Research'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <DialogContentText>
                                Enter a search query or a product to analyze the
                                current supply on Amazon:
                            </DialogContentText>

                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <TextField
                                            label={'Amazon Search Query'}
                                            value={searchString}
                                            variant="outlined"
                                            onChange={event => {
                                                setSearchString(
                                                    event.target.value
                                                )
                                            }}
                                            aria-describedby="searchString-helperText"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.main',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderWidth: 2,
                                                        borderColor:
                                                            'info.dark',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderWidth: 2,
                                                    },
                                                },
                                            }}
                                        />
                                        {advancedSettings && (
                                            <FormHelperText
                                                id="searchString-helperText"
                                                sx={{ marginTop: 2 }}
                                            >
                                                keywords used to search for the
                                                products
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                'amazonProducts'
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    sx={{
                                        marginTop: 0,
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4, marginBottom: 2 }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Graph Name'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <InputLabel id="searchSite-label">
                                            Amazon Site to Use
                                        </InputLabel>
                                        <Select
                                            labelId="searchSite-label"
                                            id="searchSite"
                                            value={searchSite}
                                            onChange={event =>
                                                setSearchSite(
                                                    event.target.value
                                                )
                                            }
                                            label="Amazon Site to Use"
                                        >
                                            <MenuItem value={'amazon.com'}>
                                                Amazon.Com (USA)
                                            </MenuItem>
                                            <MenuItem value={'amazon.co.uk'}>
                                                Amazon.Co.Uk (UK)
                                            </MenuItem>
                                            <MenuItem value={'amazon.ca'}>
                                                Amazon.Ca (Canada)
                                            </MenuItem>
                                            <MenuItem value={'amazon.de'}>
                                                Amazon.De (Germany)
                                            </MenuItem>
                                            <MenuItem value={'amazon.fr'}>
                                                Amazon.Fr (France)
                                            </MenuItem>
                                            <MenuItem value={'amazon.es'}>
                                                Amazon.Es (Spain)
                                            </MenuItem>
                                            <MenuItem value={'amazon.fr'}>
                                                Amazon.It (Italy)
                                            </MenuItem>
                                            <MenuItem value={'amazon.com.br'}>
                                                Amazon.Com.Br (Brazil)
                                            </MenuItem>
                                            <MenuItem value={'audible.com'}>
                                                Audible.Com (USA)
                                            </MenuItem>
                                            <MenuItem value={'audible.co.uk'}>
                                                Audible.Co.Uk (UK)
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 4,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={advancedSettings}
                                                    onChange={event =>
                                                        setAdvancedSettings(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Show advanced settings"
                                        />
                                    </FormControl>

                                    {advancedSettings && (
                                        <>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="numberOfResults-label">
                                                    Number of Results to Extract
                                                </InputLabel>
                                                <Select
                                                    labelId="numberOfResults-label"
                                                    id="numberOfResults"
                                                    value={numberOfResults}
                                                    onChange={event => {
                                                        setNumberOfResults(
                                                            event.target.value
                                                        )
                                                    }}
                                                    label="Number of Results to Extract"
                                                >
                                                    <MenuItem value={'10'}>
                                                        top 10 results
                                                    </MenuItem>
                                                    <MenuItem value={'20'}>
                                                        top 20 results
                                                    </MenuItem>
                                                    <MenuItem value={'40'}>
                                                        top 40 results
                                                    </MenuItem>
                                                    <MenuItem value={'60'}>
                                                        top 60 results
                                                    </MenuItem>
                                                    <MenuItem value={'80'}>
                                                        top 80 results
                                                    </MenuItem>
                                                    <MenuItem value={'100'}>
                                                        top 100 results
                                                    </MenuItem>
                                                    <MenuItem value={'200'}>
                                                        top 200 results
                                                    </MenuItem>
                                                    <MenuItem value={'400'}>
                                                        top 400 results
                                                    </MenuItem>
                                                    <MenuItem value={'600'}>
                                                        top 600 results
                                                    </MenuItem>
                                                    <MenuItem value={'800'}>
                                                        top 800 results
                                                    </MenuItem>
                                                    <MenuItem value={'1000'}>
                                                        top 1000 results
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="categorizeBy-label">
                                                    Categorize Reviews By
                                                </InputLabel>
                                                <Select
                                                    labelId="categorizeBy-label"
                                                    id="categorizeBy"
                                                    value={categorizeBy}
                                                    onChange={event => {
                                                        setCategorizeBy(
                                                            event.target.value
                                                        )
                                                    }}
                                                    label="Categorize Reviews By"
                                                    aria-describedby="categorizeBy-helperText"
                                                >
                                                    <MenuItem
                                                        value={'sentiment'}
                                                    >
                                                        Positive / Negative
                                                    </MenuItem>
                                                    <MenuItem value={'rating'}>
                                                        Star Rating
                                                    </MenuItem>
                                                </Select>
                                                <FormHelperText
                                                    id="categorizeBy-helperText"
                                                    sx={{ marginTop: 2 }}
                                                >
                                                    you will be able to use
                                                    these categories to filter
                                                    the graph
                                                </FormHelperText>
                                            </FormControl>

                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <InputLabel id="modifyAnalyzedText-label">
                                                    Build a knowledge graph from
                                                </InputLabel>
                                                <Select
                                                    labelId="modifyAnalyzedText-label"
                                                    id="modifyAnalyzedText"
                                                    value={modifyAnalyzedText}
                                                    onChange={event =>
                                                        setModifyAnalyzedText(
                                                            event.target.value
                                                        )
                                                    }
                                                    label="Build a knowledge graph from"
                                                >
                                                    <MenuItem
                                                        value={'none'}
                                                        key="1"
                                                    >
                                                        the words used in text
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={'detectEntities'}
                                                        key="2"
                                                    >
                                                        the words and detected
                                                        entities
                                                    </MenuItem>
                                                    <MenuItem
                                                        value={
                                                            'extractEntitiesOnly'
                                                        }
                                                        key="3"
                                                    >
                                                        only the detected
                                                        entities
                                                    </MenuItem>
                                                </Select>
                                                {modifyAnalyzedText !=
                                                    'none' && (
                                                    <FormHelperText
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        By default, we extract
                                                        the words used in your
                                                        text. You can also
                                                        choose to detect and tag
                                                        entities in your text
                                                        and build a graph from
                                                        them.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 2,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                id="replaceEntities"
                                                                checked={
                                                                    replaceEntities
                                                                }
                                                                onChange={event =>
                                                                    setReplaceEntities(
                                                                        event
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Replace entities with root concepts"
                                                    />
                                                    {replaceEntities && (
                                                        <FormHelperText
                                                            id="replaceEntities-helperText"
                                                            sx={{
                                                                marginTop: 1,
                                                            }}
                                                        >
                                                            When turned on, we
                                                            will not only detect
                                                            the entities, but
                                                            also replace them
                                                            with their
                                                            ontological root
                                                            concepts throughout
                                                            your text.
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!searchString.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 2 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `amazon_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 2)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `amazon_${twoFirstSearchQueryWords}`
}
